import { useQuery } from "@tanstack/react-query";
import { Card, Drawer, Tag, Typography } from "antd";
import type { TableProps } from "antd";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { apiFetch, withQuery } from "../api/client";
import { compareNumeric, compareText } from "../api/format";
import type {
  ListResponse,
  SpeakerCongregationSpeechRow,
  SpeakerCongregationStatisticRow,
} from "../api/types";
import { DataTable } from "../components/DataTable";
import { FilterBar } from "../components/FilterBar";
import type { FilterValues } from "../components/FilterBar";

/**
 * Sidebar entry "speaker_congregation_statistics" → /speaker/congregation_statistics.
 *
 * Where a speaker has been: every congregation they may be sent to, with the
 * number of assignments behind it, drilling down into the talks they gave
 * there. The counterpart is the speech statistics, which asks the same question
 * the other way round.
 *
 * The four filters reach the server, not the loaded rows: the congregation and
 * circumscription ones narrow the list itself, while the speech and language
 * ones keep only congregations where a matching talk was given — which is why
 * they are carried into the drill-down as well.
 */
export function SpeakerCongregationStatisticsPage() {
  const { t } = useTranslation();
  const [filters, setFilters] = useState<FilterValues>({});
  const [selected, setSelected] = useState<SpeakerCongregationStatisticRow | null>(null);

  const congregations = useQuery({
    queryKey: ["speaker", "congregation_statistics", filters],
    queryFn: () =>
      apiFetch<ListResponse<SpeakerCongregationStatisticRow>>(
        withQuery("/speaker/congregation_statistics", filters),
      ),
  });

  const speeches = useQuery({
    queryKey: ["speaker", "congregation_statistics", "speeches", selected?.id, filters],
    queryFn: () =>
      apiFetch<ListResponse<SpeakerCongregationSpeechRow>>(
        withQuery(`/speaker/congregation_statistics/${selected!.id}/speeches`, {
          speech_filter: filters.speech_filter ?? "",
          language_filter: filters.language_filter ?? "",
        }),
      ),
    enabled: selected !== null,
  });

  const fields = useMemo(
    () => [
      { name: "congregation_filter", label: t("activerecord.models.congregation") },
      { name: "circumscription_filter", label: t("activerecord.models.circumscription") },
      { name: "speech_filter", label: t("activerecord.models.speech") },
      { name: "language_filter", label: t("activerecord.models.language") },
    ],
    [t],
  );

  const columns = useMemo<TableProps<SpeakerCongregationStatisticRow>["columns"]>(
    () => [
      {
        title: t("activerecord.attributes.speaker.assignations"),
        dataIndex: "assignation_count",
        width: 110,
        align: "right",
        defaultSortOrder: "descend",
        sorter: (a, b) => a.assignation_count - b.assignation_count,
        render: (value: number) => (value > 0 ? <Tag color="blue">{value}</Tag> : null),
      },
      {
        title: t("activerecord.attributes.circumscription.short"),
        dataIndex: "circumscription_code",
        width: 90,
        sorter: (a, b) => compareText(a.circumscription_code, b.circumscription_code),
      },
      {
        title: t("activerecord.attributes.congregation.congregation_code"),
        dataIndex: "congregation_code",
        width: 90,
        sorter: (a, b) => compareText(a.congregation_code, b.congregation_code),
      },
      {
        title: t("activerecord.attributes.congregation.denomination"),
        dataIndex: "denomination",
        ellipsis: true,
        sorter: (a, b) => compareText(a.denomination, b.denomination),
      },
      {
        title: t("activerecord.attributes.congregation.contact_first_name"),
        dataIndex: "contact_first_name",
        width: 130,
      },
      {
        title: t("activerecord.attributes.congregation.contact_last_name"),
        dataIndex: "contact_last_name",
        width: 130,
      },
      { title: t("activerecord.attributes.congregation.phone"), dataIndex: "phone", width: 140 },
      { title: t("activerecord.attributes.congregation.mobile"), dataIndex: "mobile", width: 140 },
      {
        title: t("activerecord.attributes.congregation.email"),
        dataIndex: "email",
        width: 200,
        ellipsis: true,
      },
      {
        title: t("activerecord.attributes.congregation.street_number"),
        dataIndex: "street_number",
        width: 180,
        ellipsis: true,
      },
      { title: t("activerecord.attributes.congregation.zip_code"), dataIndex: "zip_code", width: 90 },
      { title: t("activerecord.attributes.congregation.city"), dataIndex: "city", width: 140 },
      { title: t("activerecord.attributes.congregation.state"), dataIndex: "state", width: 110 },
      { title: t("activerecord.attributes.congregation.country"), dataIndex: "country", width: 90 },
      {
        title: t("activerecord.attributes.congregation.congregation_phone"),
        dataIndex: "congregation_phone",
        width: 150,
      },
    ],
    [t],
  );

  const speechColumns = useMemo<TableProps<SpeakerCongregationSpeechRow>["columns"]>(
    () => [
      {
        title: t("activerecord.attributes.local_assignation.week_begin"),
        dataIndex: "week_label",
        width: 170,
        defaultSortOrder: "descend",
        // The label is built for reading; the plain date is what orders it.
        sorter: (a, b) => compareText(a.week_begin, b.week_begin),
      },
      {
        title: t("activerecord.attributes.speech.schema"),
        dataIndex: "schema",
        width: 100,
        sorter: (a, b) => compareNumeric(a.schema, b.schema),
      },
      {
        title: t("activerecord.attributes.speech.title"),
        dataIndex: "title",
        ellipsis: true,
      },
      {
        title: t("activerecord.attributes.speech.language_id"),
        dataIndex: "language_code",
        width: 100,
      },
    ],
    [t],
  );

  return (
    <Card title={t("navigation.speaker_congregation_statistics")}>
      <FilterBar fields={fields} onApply={setFilters} loading={congregations.isFetching} />

      <DataTable
        columns={columns}
        rows={congregations.data?.data}
        loading={congregations.isPending}
        error={congregations.error}
        exportAs={t("navigation.speaker_congregation_statistics")}
        onRowClick={setSelected}
      />

      <Drawer
        open={selected !== null}
        onClose={() => setSelected(null)}
        size={960}
        destroyOnHidden
        title={
          selected && (
            <Typography.Text>
              {[selected.congregation_code, selected.denomination].filter(Boolean).join(" – ")}
            </Typography.Text>
          )
        }
      >
        <Typography.Title level={5}>{t("speaker.speeches")}</Typography.Title>

        {/* Named after the congregation the visits belong to. */}
        <DataTable
          columns={speechColumns}
          rows={speeches.data?.data}
          exportAs={[selected?.congregation_code, t("speaker.speeches")].filter(Boolean).join(" · ")}
          loading={speeches.isPending}
          error={speeches.error}
          paginated={false}
        />
      </Drawer>
    </Card>
  );
}
